import React, { useState, useEffect } from 'react';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import {
  ImageIcon, Film, Layers, Lock, Plus, Trash2, GripVertical,
  ChevronRight, ExternalLink, AlertTriangle, Wand2,
} from 'lucide-react';
import { useFbWorkspace } from '@/contexts/FbWorkspaceContext';
import { facebookAdCreativesService, FbCreative, CreateAdCreativePayload, CarouselCardPayload } from '@/services/facebook-ad-creatives.service';
import { facebookAdsService } from '@/services/facebook-ads.service';
import { facebookAdAccountService, MetaAdAccount } from '@/services/facebook-ad-account.service';
import { aiCampaignService } from '@/services/ai-campaign.service';
import { ensureCampaignSaved } from '@/components/facebook/workspace/saveHelpers';
import { ModelPicker, ModelPickerValue } from '@/components/ModelPicker';
import { FormField } from '@/components/FormField';
import { Input } from '@/components/Input';
import { Select } from '@/components/Select';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { MediaUpload } from '@/components/MediaUpload';
import { LeadFormPicker } from '@/components/facebook/workspace/LeadFormPicker';

// ─── Char counter helper ──────────────────────────────────────────────────────
//
// Tiny visual nudge under text fields with a maxLength. Goes amber at 90%,
// red at 100% (which is also when the input itself stops accepting chars).
// Helps non-IT users see "I'm running out of room" before they get cut off.

const CharCount: React.FC<{ current: number; max: number }> = ({ current, max }) => {
  const ratio = max > 0 ? current / max : 0;
  return (
    <div
      className={clsx(
        'mt-1 text-right text-2xs tabular-nums',
        ratio >= 1 ? 'text-danger font-semibold' : ratio >= 0.9 ? 'text-warning' : 'text-text-muted',
      )}
    >
      {current} / {max}
    </div>
  );
};

// ─── CTA options ──────────────────────────────────────────────────────────────

const CTA_TYPES = [
  { code: 'LEARN_MORE',          label: 'Learn More' },
  { code: 'SHOP_NOW',            label: 'Shop Now' },
  { code: 'BOOK_TRAVEL',         label: 'Book Travel' },
  { code: 'CONTACT_US',          label: 'Contact Us' },
  { code: 'GET_QUOTE',           label: 'Get Quote' },
  { code: 'SIGN_UP',             label: 'Sign Up' },
  { code: 'DOWNLOAD',            label: 'Download' },
  { code: 'INSTALL_MOBILE_APP',  label: 'Install App' },
  { code: 'USE_APP',             label: 'Use App' },
  { code: 'WHATSAPP_MESSAGE',    label: 'WhatsApp' },
  { code: 'MESSAGE_PAGE',        label: 'Message Page' },
  { code: 'INSTAGRAM_MESSAGE',   label: 'Send Instagram message' },
  { code: 'CALL_NOW',            label: 'Call Now' },
  { code: 'GET_DIRECTIONS',      label: 'Get Directions' },
  { code: 'ORDER_NOW',           label: 'Order Now' },
  { code: 'APPLY_NOW',           label: 'Apply Now' },
  { code: 'DONATE_NOW',          label: 'Donate Now' },
  { code: 'OPEN_LINK',           label: 'Open Link' },
  { code: 'WATCH_MORE',          label: 'Watch More' },
  { code: 'SUBSCRIBE',           label: 'Subscribe' },
];

// Each CTA needs a different kind of destination data. Group them so the
// form can show the right input(s):
//   link    → standard URL (most CTAs)
//   app     → app-store URL (Install App / Use App)
//   whatsapp→ WhatsApp number (resolves to wa.me link)
//   phone   → phone number (resolves to tel: link)
//   message → no input — uses the Page's Messenger
//   directions → coordinates + address, sent as `fbgeo://lat, long, "address"` (a maps URL is refused)
type CtaKind = 'link' | 'app' | 'whatsapp' | 'phone' | 'message' | 'directions';
const CTA_KIND: Record<string, CtaKind> = {
  WHATSAPP_MESSAGE:   'whatsapp',
  MESSAGE_PAGE:       'message',
  INSTAGRAM_MESSAGE:  'message',
  CALL_NOW:           'phone',
  INSTALL_MOBILE_APP: 'app',
  USE_APP:            'app',
  GET_DIRECTIONS:     'directions',
};
const ctaKindOf = (code: string): CtaKind => CTA_KIND[code] ?? 'link';

// What Meta accepted per conversion location in the live CTA sweep (scripts/fb-live-matrix cta:*, 2026-09-30):
// 206 real paused creatives + ads. Chat destinations take only their own button; everything else is a
// short exclusion list. Widen a list only with a live PASS.
const CTA_ONLY: Record<string, string[]> = {
  MESSENGER:  ['MESSAGE_PAGE'],
  WHATSAPP:   ['WHATSAPP_MESSAGE'],
  PHONE_CALL: ['CALL_NOW', 'MESSAGE_PAGE', 'INSTAGRAM_MESSAGE'],
};
const CTA_REFUSED: Record<string, string[]> = {
  INSTAGRAM_DIRECT: ['DOWNLOAD', 'INSTALL_MOBILE_APP', 'USE_APP', 'CALL_NOW', 'GET_DIRECTIONS', 'OPEN_LINK', 'WATCH_MORE'],
  APP_PROMOTION:    ['WHATSAPP_MESSAGE', 'MESSAGE_PAGE', 'INSTAGRAM_MESSAGE', 'CALL_NOW', 'GET_DIRECTIONS'],
  // "App Url is only supported by App Installs objective"
  WEBSITE:          ['INSTALL_MOBILE_APP'],
  SALES_WEBSITE:    ['INSTALL_MOBILE_APP', 'USE_APP'],
  LEADS_WEBSITE:    ['INSTALL_MOBILE_APP', 'CALL_NOW'],
};
function ctaCodesFor(objective: string | undefined, destination: string | undefined): string[] {
  const all = CTA_TYPES.map(c => c.code);
  const dest = destination === 'INSTAGRAM' ? 'INSTAGRAM_DIRECT' : destination;
  if (dest && CTA_ONLY[dest]) return CTA_ONLY[dest];
  const key = objective === 'OUTCOME_APP_PROMOTION' ? 'APP_PROMOTION'
    : dest === 'WEBSITE' && objective === 'OUTCOME_SALES' ? 'SALES_WEBSITE'
    : dest === 'WEBSITE' && objective === 'OUTCOME_LEADS' ? 'LEADS_WEBSITE'
    : dest ?? '';
  const refused = CTA_REFUSED[key] ?? [];
  return all.filter(c => !refused.includes(c));
}

type MediaMode = 'image' | 'video' | 'carousel';

// ─── Carousel card editor ─────────────────────────────────────────────────────

interface CardEditorProps {
  cards: CarouselCardPayload[];
  onChange: (cards: CarouselCardPayload[]) => void;
}

function CarouselCardEditor({ cards, onChange }: CardEditorProps) {
  const update = (i: number, k: keyof CarouselCardPayload, v: string) => {
    const next = cards.map((c, idx) => idx === i ? { ...c, [k]: v } : c);
    onChange(next);
  };

  const remove = (i: number) => onChange(cards.filter((_, idx) => idx !== i));

  const add = () => {
    if (cards.length >= 10) { toast.error('Maximum 10 carousel cards'); return; }
    onChange([...cards, { name: '', link: '', image_url: '' }]);
  };

  return (
    <div className="space-y-4">
      {cards.map((card, i) => (
        <div key={i} className="rounded-xl border border-border bg-surface-alt/40 p-4 relative">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <GripVertical size={14} className="text-text-muted cursor-grab" />
              <span className="text-xs font-semibold text-text-primary">Card {i + 1}</span>
            </div>
            {cards.length > 2 && (
              <button type="button" onClick={() => remove(i)} className="text-text-muted hover:text-danger transition-colors">
                <Trash2 size={14} />
              </button>
            )}
          </div>

          <div className="space-y-3">
            <FormField label="Image">
              <MediaUpload
                mode="image"
                value={card.image_url}
                onChange={url => update(i, 'image_url', url)}
              />
            </FormField>

            <FormField label="Headline (max 40 chars)">
              <Input
                value={card.name}
                maxLength={40}
                onChange={e => update(i, 'name', e.target.value)}
                placeholder="e.g. Summer Collection"
              />
              <CharCount current={card.name.length} max={40} />
            </FormField>

            <FormField label="Description (max 25 chars)">
              <Input
                value={card.description ?? ''}
                maxLength={25}
                onChange={e => update(i, 'description', e.target.value)}
                placeholder="e.g. Shop now"
              />
              <CharCount current={(card.description ?? '').length} max={25} />
            </FormField>

            <FormField label="Link URL">
              <Input
                type="url"
                value={card.link}
                onChange={e => update(i, 'link', e.target.value)}
                placeholder="https://example.com/product"
              />
            </FormField>

            <FormField label="CTA button">
              <Select value={card.call_to_action_type ?? ''} onChange={e => update(i, 'call_to_action_type', e.target.value)}>
                <option value="">No CTA</option>
                {CTA_TYPES.map(c => <option key={c.code} value={c.code}>{c.label}</option>)}
              </Select>
            </FormField>
          </div>
        </div>
      ))}

      {cards.length < 10 && (
        <button
          type="button"
          onClick={add}
          className="flex items-center gap-2 w-full justify-center rounded-xl border-2 border-dashed border-border py-3 text-xs font-medium text-text-muted hover:border-primary hover:text-primary transition-colors"
        >
          <Plus size={13} /> Add card ({cards.length}/10)
        </button>
      )}
    </div>
  );
}

// ─── Live FB preview mockup ───────────────────────────────────────────────────

interface PreviewProps {
  name: string;
  headline: string;
  description: string;
  message: string;
  imageUrl: string;
  cta: string;
  cards?: CarouselCardPayload[];
  mode: MediaMode;
}

function FbPreview({ name, headline, description, message, imageUrl, cta, cards, mode }: PreviewProps) {
  const ctaLabel = CTA_TYPES.find(c => c.code === cta)?.label ?? 'Learn More';

  return (
    <div className="rounded-2xl border border-border bg-surface overflow-hidden shadow-soft w-full">
      {/* FB post header — `min-w-0` on the text column so the flex row
          shrinks the text instead of overflowing the whole card and
          forcing the phone-mock width to collapse. */}
      <div className="flex items-center gap-2.5 p-3 border-b border-border">
        <div className="w-9 h-9 shrink-0 rounded-full bg-primary-soft flex items-center justify-center">
          <span className="text-xs font-bold text-primary">P</span>
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-xs font-semibold text-text-primary truncate">Your Page</div>
          <div className="flex items-center gap-1 text-2xs text-text-muted">
            <span className="truncate">Sponsored</span>
            <span>·</span>
            <ExternalLink size={9} className="shrink-0" />
          </div>
        </div>
      </div>

      {/* Body text */}
      {message && (
        <p className="px-3 py-2 text-xs text-text-primary line-clamp-3">{message}</p>
      )}

      {/* Image / Carousel */}
      {mode === 'carousel' && cards && cards.length > 0 ? (
        <div className="flex gap-1 overflow-x-auto px-1 pb-1">
          {cards.map((c, i) => (
            <div key={i} className="shrink-0 w-36 rounded-lg overflow-hidden border border-border">
              {c.image_url
                ? <img src={c.image_url} alt="" className="w-full h-28 object-cover" />
                : <div className="w-full h-28 bg-surface-alt flex items-center justify-center"><ImageIcon size={20} className="text-text-muted" /></div>
              }
              <div className="p-2 bg-surface border-t border-border">
                <div className="text-2xs font-semibold text-text-primary truncate">{c.name || 'Headline'}</div>
                <div className="text-2xs text-text-muted truncate">{c.description || ''}</div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-surface-alt/60">
          {imageUrl ? (
            <img src={imageUrl} alt="" className="w-full max-h-52 object-cover" />
          ) : (
            <div className="h-40 flex items-center justify-center bg-surface-alt">
              {mode === 'video'
                ? <Film size={32} className="text-text-muted" />
                : <ImageIcon size={32} className="text-text-muted" />
              }
            </div>
          )}
        </div>
      )}

      {/* Ad footer — line-clamp-2 lets short copy fit on one line, longer
          copy wrap to two. Previously `truncate` was producing "WhisperC..."
          on a 12-character headline because the column was being squeezed. */}
      <div className="p-3 flex items-start justify-between gap-2 border-t border-border">
        <div className="min-w-0 flex-1">
          <div className="text-xs font-semibold text-text-primary line-clamp-2">{headline || 'Headline'}</div>
          <div className="text-2xs text-text-muted line-clamp-2">{description || 'Description'}</div>
        </div>
        {cta && (
          <div className="shrink-0 rounded-md bg-info-soft border border-info/30 px-2.5 py-1 text-2xs font-semibold text-info">
            {ctaLabel}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── AdFormPanel ─────────────────────────────────────────────────────────────

export const AdFormPanel: React.FC = () => {
  const {
    activeAdSetId, activeAdId, activeAd, activeAdSet, campaign,
    isSubmitting, setIsSubmitting, lockAd, unlockAd,
    lockCampaign, updateAdForm, setActiveLevel, platform, appPromotion,
  } = useFbWorkspace();
  const isLocked = activeAd?.isLocked ?? false;
  // IG workspace forces every ad to carry the Instagram actor so the ad
  // serves to IG users. The picker that normally lets the user uncheck
  // "Run on Instagram" is hidden in IG mode — there's no FB surface to
  // fall back to, and an IG ad without an IG actor never delivers.
  const isIgWorkspace = platform === 'instagram';

  // Hydrate from formData so switching ads (which remounts via key) doesn't
  // wipe values, and switching back restores what was typed.
  const fd = (activeAd?.formData ?? {}) as Record<string, unknown>;
  const fdMode = (fd.mode as MediaMode | undefined);
  const fdTab = (fd.tab as 'pick' | 'build' | undefined);
  const fdCards = fd.cards as CarouselCardPayload[] | undefined;

  const [tab, setTab] = useState<'pick' | 'build'>(fdTab ?? 'build');
  const [mode, setMode] = useState<MediaMode>(fdMode ?? 'image');

  // Existing creative picker
  const [creatives, setCreatives] = useState<FbCreative[]>([]);
  const [pickedCreative, setPickedCreative] = useState<string>((fd.picked_creative as string) ?? '');

  // Build state — initialised from formData
  const [adName, setAdName] = useState((fd.ad_name as string) ?? activeAd?.name ?? '');
  const [creativeName, setCreativeName] = useState((fd.creative_name as string) ?? '');
  const [link, setLink] = useState(appPromotion?.storeUrl ?? (fd.link as string) ?? '');
  const [headline, setHeadline] = useState((fd.headline as string) ?? '');
  const [description, setDescription] = useState((fd.description as string) ?? '');
  const [message, setMessage] = useState((fd.message as string) ?? '');
  const [imageUrl, setImageUrl] = useState((fd.image_url as string) ?? '');
  const [videoUrl, setVideoUrl] = useState((fd.video_url as string) ?? '');
  const [thumbnailUrl, setThumbnailUrl] = useState((fd.thumbnail_url as string) ?? '');
  const [cta, setCta] = useState(appPromotion ? 'INSTALL_MOBILE_APP' : (fd.cta as string) ?? 'LEARN_MORE');
  const [whatsappNumber, setWhatsappNumber] = useState(
    (fd.whatsapp_number as string) ?? ((activeAdSet?.formData as Record<string, unknown> | undefined)?.whatsapp_phone_number as string) ?? '',
  );
  const [whatsappPrefill, setWhatsappPrefill] = useState((fd.whatsapp_prefill as string) ?? '');
  const [phoneNumber, setPhoneNumber] = useState((fd.phone_number as string) ?? '');
  const [dirLat, setDirLat] = useState((fd.directions_lat as string) ?? '');
  const [dirLng, setDirLng] = useState((fd.directions_lng as string) ?? '');
  const [dirAddress, setDirAddress] = useState((fd.directions_address as string) ?? '');
  // IG workspace pins this to true. Outside IG mode the user can still toggle.
  const [runOnIg, setRunOnIg] = useState<boolean>(isIgWorkspace ? true : (fd.run_on_ig === undefined ? true : !!fd.run_on_ig));
  const [cards, setCards] = useState<CarouselCardPayload[]>(
    (fdCards && fdCards.length >= 2) ? fdCards : [
      { name: '', link: '', image_url: '' },
      { name: '', link: '', image_url: '' },
    ],
  );
  const [leadFormId, setLeadFormId] = useState<string>((fd.lead_form_id as string) ?? '');
  const [aiModel, setAiModel] = useState<ModelPickerValue | undefined>(undefined);
  const [enhancing, setEnhancing] = useState(false);

  // Sync any local change back to context so navigation never loses input.
  const sync = (k: string, v: unknown) => {
    if (activeAdSetId && activeAdId) updateAdForm(activeAdSetId, activeAdId, { [k]: v });
  };

  // AI enhance — takes whatever's in name/headline/description/message,
  // sends it to the AI with the campaign objective + brand context, then
  // writes the polished response back into the form.
  const enhance = async () => {
    setEnhancing(true);
    try {
      const r = await aiCampaignService.enhanceAdCopy({
        objective: campaign.objective || undefined,
        draftName: adName || undefined,
        draftHeadline: headline || undefined,
        draftDescription: description || undefined,
        draftMessage: message || undefined,
        landingPageUrl: link || undefined,
        ai_provider: aiModel?.provider,
        ai_model: aiModel?.model,
      });
      if (r.name)        { setAdName(r.name); sync('ad_name', r.name); sync('name', r.name); }
      if (r.headline)    { setHeadline(r.headline); sync('headline', r.headline); }
      if (r.description) { setDescription(r.description); sync('description', r.description); }
      if (r.message)     { setMessage(r.message); sync('message', r.message); }
      toast.success('Enhanced with AI');
    } catch {
      // Axios interceptor toasts the backend message; avoid duplicates.
    } finally { setEnhancing(false); }
  };

  // Only the Instant Form location is a lead-form ad. Leads → Website / Calls / Messenger / App ads use
  // their own CTAs (Calls needs Call Now), and forcing a form on them made those locations unbuildable.
  const adSetFd = (activeAdSet?.formData ?? {}) as Record<string, unknown>;
  const adSetGoal = adSetFd.optimization_goal as string | undefined;
  const adSetDest = adSetFd.destination_type as string | undefined;
  const isLeadGen = campaign.objective === 'OUTCOME_LEADS'
    && (!adSetGoal || adSetGoal === 'LEAD_GENERATION' || adSetGoal === 'QUALITY_LEAD')
    && (!adSetDest || adSetDest === 'ON_AD');
  // Lead Gen ads only support these CTAs per Meta docs
  const LEAD_GEN_CTAS = ['SIGN_UP', 'LEARN_MORE', 'APPLY_NOW', 'GET_QUOTE', 'SUBSCRIBE', 'DOWNLOAD', 'BOOK_TRAVEL'];
  const allowedCtas = isLeadGen ? LEAD_GEN_CTAS : ctaCodesFor(campaign.objective, adSetDest);
  const ctaOptions = CTA_TYPES.filter((c) => allowedCtas.includes(c.code));
  // Keep the picked CTA valid for this location — Meta refuses the rest at ad create.
  useEffect(() => {
    if (appPromotion || allowedCtas.includes(cta)) return;
    const next = isLeadGen ? 'SIGN_UP' : allowedCtas[0];
    if (next) { setCta(next); sync('cta', next); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLeadGen, allowedCtas.join(',')]);

  const [account, setAccount] = useState<MetaAdAccount | null>(null);

  useEffect(() => {
    facebookAdCreativesService.list().then(r => setCreatives(r.data)).catch(() => undefined);
    facebookAdAccountService.get().then(setAccount).catch(() => undefined);
  }, []);

  const handleSave = async () => {
    if (!activeAdSetId || !activeAdId) return;
    if (appPromotion && (tab !== 'build' || mode === 'carousel')) { toast.error('Build an image or video creative for this app.'); return; }

    // Parent must exist on Meta. The ad set's per-objective extra fields live
    // in TrafficAdSetForm/etc local state, so we can't reliably reconstruct
    // the ad-set payload from here. Bounce the user back to ad-set view with
    // a clear CTA — this matches the hierarchy invariant in §1.4 of the doc.
    if (!activeAdSet?.apiId) {
      toast.error('Save the ad set first — switching you to it');
      setActiveLevel('adset', activeAdSetId, null);
      return;
    }

    // Campaign should already be locked (because saving the ad set would have
    // saved it), but auto-save it as a safety net so the chain never breaks.
    setIsSubmitting(true);
    try {
      const campaignApiId = await ensureCampaignSaved(campaign, lockCampaign, appPromotion?.accountUid);
      if (!campaignApiId) { setIsSubmitting(false); return; }

      let creativeId: string;

      if (tab === 'pick') {
        if (!pickedCreative) { toast.error('Select a creative'); setIsSubmitting(false); return; }
        creativeId = pickedCreative;
      } else {
        // Build creative
        if (!adName) { toast.error('Ad name is required'); setIsSubmitting(false); return; }
        if (!creativeName) { toast.error('Creative name is required'); setIsSubmitting(false); return; }
        if (isLeadGen && !leadFormId) {
          toast.error('Pick a Lead Gen form for this Leads campaign');
          setIsSubmitting(false);
          return;
        }

        const cp: CreateAdCreativePayload = {
          expected_account_uid: appPromotion?.accountUid,
          name: creativeName,
          headline,
          description,
          message,
          call_to_action_type: cta,
          instagram_actor_id: (runOnIg && account?.instagramAccountId) ? account.instagramAccountId : undefined,
          // Inherit the Page from the ad set's `Where ads will run` picker so
          // the user only picks once per ad set; backend resolves & validates.
          adset_id: activeAdSet.apiId,
        };

        if (mode === 'carousel') {
          if (cards.length < 2) { toast.error('At least 2 cards required'); setIsSubmitting(false); return; }
          cp.child_attachments = cards;
          cp.multi_share_optimized = 'true';
        } else {
          // Compose the destination link from CTA-specific inputs. Each kind
          // builds its own link so the user only fills the field that matches
          // their CTA — the form, not the user, knows the URL conventions.
          const kind = ctaKindOf(cta);
          let destLink = '';
          if (kind === 'whatsapp') {
            const digits = whatsappNumber.replace(/\D/g, '');
            if (!digits) { toast.error('WhatsApp number is required for the WhatsApp CTA'); setIsSubmitting(false); return; }
            const text = whatsappPrefill ? `&text=${encodeURIComponent(whatsappPrefill)}` : '';
            destLink = `https://api.whatsapp.com/send/?phone=${digits}${text}`;
          } else if (kind === 'phone') {
            const cleaned = phoneNumber.replace(/[^\d+]/g, '');
            if (!cleaned) { toast.error('Phone number is required for the Call Now CTA'); setIsSubmitting(false); return; }
            destLink = `tel:${cleaned.startsWith('+') ? cleaned : `+${cleaned}`}`;
          } else if (kind === 'message') {
            // Click-to-Messenger uses the Page's Messenger; Meta still needs a
            // syntactically-valid link in the creative. The Page deep link is
            // auto-resolved server-side, so a placeholder is enough here.
            destLink = 'https://m.me/';
          } else if (kind === 'directions') {
            const lat = Number(dirLat); const lng = Number(dirLng);
            if (!dirLat || !dirLng || !Number.isFinite(lat) || !Number.isFinite(lng) || Math.abs(lat) > 90 || Math.abs(lng) > 180) {
              toast.error('Enter the business location as latitude and longitude for Get Directions'); setIsSubmitting(false); return;
            }
            const address = dirAddress.trim().replace(/"/g, "'");
            destLink = `fbgeo://${lat}, ${lng}${address ? `, "${address}"` : ''}`;
          } else {
            // 'link' | 'app' — user-supplied URL.
            if (!link) { toast.error('Destination link is required'); setIsSubmitting(false); return; }
            destLink = link;
          }

          if (mode === 'video') {
            cp.video_url = videoUrl;
            cp.thumbnail_url = thumbnailUrl || undefined;
          } else {
            cp.image_url = imageUrl;
          }
          cp.link = destLink;
        }

        // Lead Gen — overrides destination link with the Instant Form
        if (isLeadGen && leadFormId) {
          cp.lead_gen_form_id = leadFormId;
        }

        const cr = await facebookAdCreativesService.create(cp);
        creativeId = cr.id;
      }

      const adRes = await facebookAdsService.create({
        expected_account_uid: appPromotion?.accountUid,
        name: adName || `Ad ${Date.now()}`,
        adset_id: activeAdSet.apiId,
        creative_id: creativeId,
        status: 'PAUSED',
      });

      lockAd(activeAdSetId, activeAdId, adRes.id, adRes.id);
      toast.success('Ad saved');
    } catch {
      // Axios interceptor already toasted the backend message; avoid duplicates.
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    // No `max-w-5xl` here — the preview column was getting squeezed when
    // the form-area width already had a ContextHelpPanel competing on the
    // right. Let the panel use whatever the workspace gives it.
    // `min-w-0` on the form column is essential — without it, wide form
    // children (image previews at full width, breadcrumbs) push past the
    // flex-1 share and starve the preview column down to ~80-100px,
    // which is what surfaced as "Yo / Sp... / WhisperC..." truncation.
    <div className="flex gap-6">
      {/* Left — form */}
      <div className="flex-1 min-w-0 space-y-5">
        {/* AI enhance bar — copy on its own row, controls beneath. A single
            flex-wrap row let the model picker and button crush the description
            down to a few characters per line before anything wrapped. */}
        {!isLocked && (
          <div className="rounded-2xl border border-primary/30 bg-primary-soft/40 p-3">
            <div className="flex items-start gap-2">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary text-white">
                <Wand2 size={14} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-text-primary">Enhance with AI</div>
                <div className="text-2xs text-text-muted">
                  Type rough drafts in the ad fields, pick a model, then click Enhance — we'll rewrite the headline, description and primary text in one shot.
                </div>
              </div>
            </div>
            <div className="mt-2.5 flex flex-wrap items-end justify-end gap-2">
              <ModelPicker value={aiModel} onChange={setAiModel} size="sm" label="AI model" />
              <Button
                variant="primary"
                size="sm"
                onClick={enhance}
                loading={enhancing}
                iconLeft={<Wand2 size={13} />}
              >
                Enhance
              </Button>
            </div>
          </div>
        )}

        <Card padded>
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-sm font-semibold text-text-primary">Ad</h4>
            {isLocked && (
              <span className="flex items-center gap-1 text-xs text-warning">
                <Lock size={12} /> Locked
                <button type="button" onClick={() => activeAdSetId && activeAdId && unlockAd(activeAdSetId, activeAdId)} className="ml-1 underline">Edit</button>
              </span>
            )}
          </div>

          <FormField label="Ad name" required>
            <Input
              value={adName}
              disabled={isLocked}
              onChange={e => { setAdName(e.target.value); sync('ad_name', e.target.value); sync('name', e.target.value); }}
              placeholder="e.g. Spring — Carousel — UAE"
            />
          </FormField>
        </Card>

        {/* Creative tabs */}
        <Card padded>
          <div className="flex border-b border-border mb-4">
            {[
              { k: 'pick' as const, l: 'Use existing creative' },
              { k: 'build' as const, l: 'Build new creative' },
            ].map(t => (
              <button
                key={t.k}
                type="button"
                onClick={() => { setTab(t.k); sync('tab', t.k); }}
                disabled={isLocked || (!!appPromotion && t.k === 'pick')}
                className={clsx(
                  'px-4 py-2.5 text-xs font-medium border-b-2 -mb-px transition-colors',
                  tab === t.k ? 'border-primary text-primary' : 'border-transparent text-text-muted hover:text-text-primary',
                )}
              >
                {t.l}
              </button>
            ))}
          </div>

          {tab === 'pick' ? (
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {creatives.length === 0 && (
                <p className="text-xs text-text-muted text-center py-6">No creatives yet — build one in the next tab.</p>
              )}
              {creatives.map(c => (
                <button
                  key={c.id}
                  type="button"
                  disabled={isLocked}
                  onClick={() => { setPickedCreative(c.id); sync('picked_creative', c.id); }}
                  className={clsx(
                    'flex items-center gap-3 w-full rounded-xl border p-3 text-left transition-colors',
                    pickedCreative === c.id ? 'border-primary bg-primary-soft' : 'border-border hover:bg-surface-alt',
                  )}
                >
                  {c.imagekit_url || c.thumbnail_url || c.image_url
                    ? <img src={c.imagekit_url ?? c.thumbnail_url ?? c.image_url} alt="" className="w-12 h-12 rounded-lg object-cover border border-border" />
                    : <div className="w-12 h-12 rounded-lg bg-surface-alt flex items-center justify-center"><ImageIcon size={18} className="text-text-muted" /></div>
                  }
                  <div className="min-w-0">
                    <div className="text-xs font-semibold text-text-primary truncate">{c.name}</div>
                    <div className="text-2xs text-text-muted capitalize">{c.media_kind}</div>
                  </div>
                  {pickedCreative === c.id && <ChevronRight size={14} className="ml-auto text-primary shrink-0" />}
                </button>
              ))}
            </div>
          ) : (
            <div className="space-y-5">
              <FormField label="Creative name" required>
                <Input
                  value={creativeName}
                  disabled={isLocked}
                  onChange={e => { setCreativeName(e.target.value); sync('creative_name', e.target.value); }}
                  placeholder="e.g. Spring — Image A"
                />
              </FormField>

              {/* Mode picker */}
              <div>
                <p className="text-xs font-medium text-text-secondary mb-2">Creative format</p>
                <div className="flex gap-2">
                  {([
                    { m: 'image' as MediaMode, icon: <ImageIcon size={14} />, label: 'Image' },
                    { m: 'video' as MediaMode, icon: <Film size={14} />,      label: 'Video' },
                    { m: 'carousel' as MediaMode, icon: <Layers size={14} />, label: 'Carousel' },
                  ]).map(opt => (
                    <button
                      key={opt.m}
                      type="button"
                      disabled={isLocked || (!!appPromotion && opt.m === 'carousel')}
                      onClick={() => { setMode(opt.m); sync('mode', opt.m); }}
                      className={clsx(
                        'flex items-center gap-1.5 rounded-lg border px-3 py-2 text-xs font-medium transition-colors',
                        mode === opt.m ? 'border-primary bg-primary-soft text-primary' : 'border-border text-text-muted hover:border-border-strong',
                      )}
                    >
                      {opt.icon} {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Common text fields */}
              {mode !== 'carousel' && (
                <>
                  {/* CTA picker first — drives which destination field appears below. */}
                  <FormField label="Call to action" required hint={isLeadGen ? 'Lead Gen ads only support a subset of CTAs.' : 'Pick what the button does — the field below changes to match.'}>
                    <Select value={cta} disabled={isLocked || !!appPromotion} onChange={e => { setCta(e.target.value); sync('cta', e.target.value); }}>
                      {ctaOptions.map(c => <option key={c.code} value={c.code}>{c.label}</option>)}
                    </Select>
                  </FormField>

                  {/* CTA-aware destination — only the relevant input is visible. */}
                  {ctaKindOf(cta) === 'link' && (
                    <FormField label="Destination link" required hint="The URL the user lands on after clicking the button.">
                      <Input
                        type="url"
                        value={link}
                        disabled={isLocked || !!appPromotion}
                        onChange={e => { setLink(e.target.value); sync('link', e.target.value); }}
                        placeholder="https://example.com"
                      />
                    </FormField>
                  )}

                  {ctaKindOf(cta) === 'app' && (
                    <FormField label="App store URL" required hint="Google Play or App Store URL for the app.">
                      <Input
                        type="url"
                        value={link}
                        disabled={isLocked}
                        onChange={e => { setLink(e.target.value); sync('link', e.target.value); }}
                        placeholder="https://play.google.com/store/apps/details?id=…"
                        readOnly={!!appPromotion}
                      />
                    </FormField>
                  )}

                  {ctaKindOf(cta) === 'whatsapp' && (
                    <>
                      <FormField label="WhatsApp number" required hint="International format with country code, no spaces (e.g. 971501234567).">
                        <Input
                          value={whatsappNumber}
                          disabled={isLocked}
                          inputMode="numeric"
                          onChange={e => {
                            const v = e.target.value.replace(/[^\d+]/g, '');
                            setWhatsappNumber(v);
                            sync('whatsapp_number', v);
                          }}
                          placeholder="971501234567"
                        />
                      </FormField>
                      <FormField label="Pre-filled message (optional)" hint="What the WhatsApp chat opens with — e.g. ‘Hi, I’m interested in your offer.’">
                        <Input
                          value={whatsappPrefill}
                          disabled={isLocked}
                          maxLength={500}
                          onChange={e => { setWhatsappPrefill(e.target.value); sync('whatsapp_prefill', e.target.value); }}
                          placeholder="Hi, I’m interested in…"
                        />
                      </FormField>
                    </>
                  )}

                  {ctaKindOf(cta) === 'phone' && (
                    <FormField label="Phone number" required hint="International format with country code, no spaces (e.g. +971501234567).">
                      <Input
                        value={phoneNumber}
                        disabled={isLocked}
                        onChange={e => {
                          const v = e.target.value.replace(/[^\d+]/g, '');
                          setPhoneNumber(v);
                          sync('phone_number', v);
                        }}
                        placeholder="+971501234567"
                      />
                    </FormField>
                  )}

                  {ctaKindOf(cta) === 'message' && (
                    <div className="rounded-lg border border-info/30 bg-info-soft px-3 py-2.5 text-xs text-text-secondary">
                      <strong className="text-text-primary">Routes to your Page’s Messenger.</strong>{' '}
                      No link needed — clicks open a Messenger chat with the Facebook Page on your ad set.
                    </div>
                  )}

                  {ctaKindOf(cta) === 'directions' && (
                    <div className="space-y-2">
                      <div className="grid grid-cols-2 gap-3">
                        <FormField label="Latitude" required>
                          <Input inputMode="decimal" value={dirLat} disabled={isLocked} placeholder="25.0800"
                            onChange={e => { setDirLat(e.target.value); sync('directions_lat', e.target.value); }} />
                        </FormField>
                        <FormField label="Longitude" required>
                          <Input inputMode="decimal" value={dirLng} disabled={isLocked} placeholder="55.1400"
                            onChange={e => { setDirLng(e.target.value); sync('directions_lng', e.target.value); }} />
                        </FormField>
                      </div>
                      <FormField label="Address" hint="Shown with the pin. Meta needs coordinates — a Google Maps link is refused. In Google Maps, right-click the place to copy them.">
                        <Input value={dirAddress} disabled={isLocked} placeholder="Dubai Marina Walk, Dubai"
                          onChange={e => { setDirAddress(e.target.value); sync('directions_address', e.target.value); }} />
                      </FormField>
                    </div>
                  )}

                  <FormField label="Message / Body text">
                    <textarea
                      value={message}
                      disabled={isLocked}
                      onChange={e => { setMessage(e.target.value); sync('message', e.target.value); }}
                      rows={3}
                      maxLength={2200}
                      placeholder="Write your ad message…"
                      className="w-full rounded-lg border border-border bg-surface px-3.5 py-2.5 text-sm text-text-primary placeholder:text-text-muted resize-none focus:outline-none focus:border-primary focus:ring-4 focus:ring-primary-soft disabled:opacity-60"
                    />
                    <CharCount current={message.length} max={2200} />
                  </FormField>
                  <div className="grid grid-cols-2 gap-3">
                    <FormField label="Headline">
                      <Input value={headline} disabled={isLocked} onChange={e => { setHeadline(e.target.value); sync('headline', e.target.value); }} placeholder="e.g. Shop the sale" maxLength={255} />
                      <CharCount current={headline.length} max={255} />
                    </FormField>
                    <FormField label="Description">
                      <Input value={description} disabled={isLocked} onChange={e => { setDescription(e.target.value); sync('description', e.target.value); }} placeholder="e.g. Up to 40% off" maxLength={255} />
                      <CharCount current={description.length} max={255} />
                    </FormField>
                  </div>
                </>
              )}

              {mode === 'carousel' && (
                <>
                  <FormField label="Body message">
                    <textarea
                      value={message}
                      disabled={isLocked}
                      onChange={e => { setMessage(e.target.value); sync('message', e.target.value); }}
                      rows={2}
                      maxLength={2200}
                      placeholder="Write your ad message…"
                      className="w-full rounded-lg border border-border bg-surface px-3.5 py-2.5 text-sm text-text-primary placeholder:text-text-muted resize-none focus:outline-none focus:border-primary focus:ring-4 focus:ring-primary-soft disabled:opacity-60"
                    />
                    <CharCount current={message.length} max={2200} />
                  </FormField>
                  <CarouselCardEditor cards={cards} onChange={(c) => { setCards(c); sync('cards', c); }} />
                </>
              )}

              {/* Media upload */}
              {mode === 'image' && (
                <FormField label="Image">
                  <MediaUpload mode="image" value={imageUrl} onChange={url => { setImageUrl(url); sync('image_url', url); }} />
                </FormField>
              )}
              {mode === 'video' && (
                <>
                  <FormField label="Video">
                    <MediaUpload mode="video" value={videoUrl} onChange={url => { setVideoUrl(url); sync('video_url', url); }} />
                  </FormField>
                  <FormField label="Thumbnail (optional)">
                    <MediaUpload mode="image" value={thumbnailUrl} onChange={url => { setThumbnailUrl(url); sync('thumbnail_url', url); }} />
                  </FormField>
                </>
              )}

              {/* CTA picker now lives above the destination field (drives it). */}

              {/* Lead Gen form picker — only for OUTCOME_LEADS objective */}
              {isLeadGen && mode !== 'carousel' && (
                <LeadFormPicker
                  value={leadFormId}
                  onChange={(id) => { setLeadFormId(id); sync('lead_form_id', id); }}
                  disabled={isLocked}
                />
              )}

              {/* Instagram toggle — hidden in IG workspace because the ad
                  is IG-targeted by definition; pin it on and tell the user
                  what's happening + flag if no IG account is connected. */}
              {!isIgWorkspace && account?.instagramAccountId && (
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    className="accent-primary"
                    checked={runOnIg}
                    onChange={e => { setRunOnIg(e.target.checked); sync('run_on_ig', e.target.checked); }}
                    disabled={isLocked}
                  />
                  <span className="text-xs font-medium text-text-secondary">
                    Also run on Instagram
                    <span className="ml-1 text-text-muted">(@{account.instagramAccountId})</span>
                  </span>
                </label>
              )}
              {isIgWorkspace && (
                account?.instagramAccountId ? (
                  <div className="flex items-start gap-2 rounded-lg border border-info/30 bg-info-soft px-3 py-2 text-2xs text-text-secondary">
                    <span className="font-semibold text-info">Instagram actor:</span>
                    <span>This ad will run as <strong className="text-text-primary">@{account.instagramAccountId}</strong> on every IG placement. Required for IG delivery.</span>
                  </div>
                ) : (
                  <div className="flex items-start gap-2 rounded-lg border border-danger/30 bg-danger-soft px-3 py-2 text-2xs text-text-secondary">
                    <span className="font-semibold text-danger">No Instagram account linked.</span>
                    <span>Meta requires an IG actor on every Instagram ad — without one, the ad will not serve. Connect an Instagram account on the Ad Account Settings page before saving.</span>
                  </div>
                )
              )}
            </div>
          )}
        </Card>

        {!isLocked && !activeAdSet?.apiId && (
          <div className="flex items-start gap-2 rounded-lg border border-warning/40 bg-warning-soft px-3 py-2.5 text-xs">
            <AlertTriangle size={14} className="mt-0.5 shrink-0 text-warning" />
            <div className="flex-1">
              <div className="font-semibold text-text-primary">Ad set isn't on Meta yet</div>
              <p className="mt-0.5 text-text-muted">
                Save the parent ad set first — its targeting/optimization fields
                live there.
              </p>
              <button
                type="button"
                onClick={() => activeAdSetId && setActiveLevel('adset', activeAdSetId, null)}
                className="mt-1.5 inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline"
              >
                Go to ad set →
              </button>
            </div>
          </div>
        )}

        {!isLocked && (
          <Button
            variant="primary"
            loading={isSubmitting}
            onClick={handleSave}
            disabled={!activeAdSet?.apiId}
          >
            Save Ad
          </Button>
        )}
      </div>

      {/* Right — Live preview. Widened from w-72 → w-80 (288 → 320px) so the
          phone-mock content (name, sponsored, headline, description) renders
          without wrapping/truncating. Hidden below `lg:` to give small
          viewports back to the form. */}
      {/* 1440, not lg. The workspace carries two 256px rails, so this 320px
          preview only fits once the window can still leave the form ~500px. */}
      <div className="hidden min-[1440px]:block w-80 shrink-0">
        <div className="sticky top-6 space-y-3">
          <p className="text-xs font-semibold text-text-secondary">Live preview</p>
          <FbPreview
            name={adName}
            headline={headline}
            description={description}
            message={message}
            imageUrl={mode === 'image' ? imageUrl : (mode === 'carousel' ? '' : '')}
            cta={cta}
            cards={mode === 'carousel' ? cards : undefined}
            mode={mode}
          />
        </div>
      </div>
    </div>
  );
};
